import type { Catalog, Me } from "../types.js";

export class ApiError extends Error {
  /** LOW-2: true when a 409 finalize conflict carried `applied` (account change already applied). */
  public readonly applied?: boolean;
  /** LOW-2: the applied account state carried with an `applied` conflict. */
  public readonly accountState?: unknown;
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    extras?: { applied?: boolean; accountState?: unknown }
  ) {
    super(message);
    this.name = "ApiError";
    this.applied = extras?.applied;
    this.accountState = extras?.accountState;
  }
}

export interface ApiClient {
  me(): Promise<Me>;
  catalog(): Promise<Catalog>;
  detail(service: string, stage: string, module: string, resource: string, id: string): Promise<unknown>;
  action(
    service: string,
    stage: string,
    module: string,
    resource: string,
    id: string,
    action: string,
    body: Record<string, unknown>
  ): Promise<unknown>;
  linkStart(service: string, stage: string, username: string): Promise<{ status: "SENT_IF_EXISTS" }>;
  linkVerify(
    service: string,
    stage: string,
    code: string
  ): Promise<{ verified: boolean; username: string; userId: string; maskedPhone?: string }>;
  listAdmins(): Promise<Array<{ adminId: string; email: string; assignments: Array<{ service: string; stage: string; roles: string[] }> }>>;
  setAssignment(adminId: string, service: string, stage: string, roles: string[]): Promise<unknown>;
  /** Call a hosted-feature admin surface: /api/s/{service}/{stage}/f/{feature}/{surface}. */
  feature(
    service: string,
    stage: string,
    feature: string,
    surface: string,
    method: string,
    body?: Record<string, unknown>
  ): Promise<unknown>;
  /** Read an ML sub-resource: /api/s/{service}/{stage}/ml/{path}. */
  mlGet(service: string, stage: string, path: string): Promise<unknown>;
  /** Invoke an ML program action: start-run | promote | rollback | set-serving. */
  mlAction(service: string, stage: string, programId: string, action: string, body: Record<string, unknown>): Promise<unknown>;
  /** Read scoring policy & parameter metadata: /api/s/{service}/{stage}/intelligence/policy. */
  intelligencePolicy(service: string, stage: string): Promise<unknown>;
  /** Update scoring policy: /api/s/{service}/{stage}/intelligence/policy. */
  updateIntelligencePolicy(service: string, stage: string, body: Record<string, unknown>): Promise<unknown>;
  /** Run simulation against current or candidate policy: /api/s/{service}/{stage}/intelligence/simulate. */
  simulateIntelligence(service: string, stage: string, body: Record<string, unknown>): Promise<unknown>;
}

type FetchFn = typeof fetch;

/**
 * Encode an object as a single URL-safe base64url(JSON) string. GET feature surfaces carry their
 * parameters this way (a browser cannot send a GET body), in ONE `q` query parameter, so nested
 * filter objects (e.g. the moderation queue filter) survive the round-trip. The server decodes `q`
 * back into the dispatcher body (api/src/handler.ts). UTF-8 safe (TextEncoder), no padding.
 */
export function encodeQueryParam(value: unknown): string {
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * API client. The browser only ever calls PORTAL routes; it never supplies a backend path or
 * method. Resource ids are passed as path segments and validated+substituted server-side.
 */
export function createApiClient(opts: { apiBase: string; getToken: () => string | null; fetchFn?: FetchFn }): ApiClient {
  const fetchFn = opts.fetchFn ?? fetch;
  const enc = encodeURIComponent;

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = opts.getToken();
    const headers: Record<string, string> = { accept: "application/json" };
    if (token) headers.authorization = `Bearer ${token}`;
    if (body !== undefined) headers["content-type"] = "application/json";

    const res = await fetchFn(`${opts.apiBase}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    let data: unknown = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null; // non-JSON body (e.g. an edge error page)
    }
    if (!res.ok) {
      // Portal envelope {error:{code,message}}, or API Gateway's {message} (e.g. the authorizer's 401).
      // HTTP/2 has no status text, so never fall back to an empty message.
      const body = data as { error?: { code?: string; message?: string; applied?: boolean; accountState?: unknown }; message?: string } | null;
      const message = body?.error?.message || body?.message || res.statusText || `Request failed (HTTP ${res.status})`;
      const code = body?.error?.code ?? (res.status === 401 ? "unauthorized" : "error");
      throw new ApiError(code, message, res.status, { applied: body?.error?.applied, accountState: body?.error?.accountState });
    }
    return data as T;
  }

  const base = (s: string, st: string, m: string, r: string, id: string): string =>
    `/s/${enc(s)}/${enc(st)}/m/${enc(m)}/r/${enc(r)}/${enc(id)}`;

  return {
    me: () => request<Me>("GET", "/me"),
    catalog: () => request<Catalog>("GET", "/catalog"),
    detail: (s, st, m, r, id) => request("GET", base(s, st, m, r, id)),
    action: (s, st, m, r, id, action, body) => request("POST", `${base(s, st, m, r, id)}/actions/${enc(action)}`, body),
    linkStart: (s, st, username) => request("POST", `/s/${enc(s)}/${enc(st)}/identity/link/start`, { username }),
    linkVerify: (s, st, code) => request("POST", `/s/${enc(s)}/${enc(st)}/identity/link/verify`, { code }),
    listAdmins: () => request("GET", "/admins"),
    setAssignment: (adminId, s, st, roles) =>
      request("PUT", `/admins/${enc(adminId)}/assignments/${enc(s)}/${enc(st)}`, { roles }),
    feature: (s, st, f, surface, method, body) => {
      const path = `/s/${enc(s)}/${enc(st)}/f/${enc(f)}/${enc(surface)}`;
      // A GET cannot carry a request body, so its parameters travel in the query string as a single
      // base64url(JSON) `q` param (supports nested filters). POST bodies are unchanged. An empty/absent
      // body produces no query string at all.
      if (method === "GET") {
        const query = body && Object.keys(body).length > 0 ? `?q=${encodeQueryParam(body)}` : "";
        return request(method, `${path}${query}`);
      }
      return request(method, path, body ?? {});
    },
    mlGet: (s, st, path) => request("GET", `/s/${enc(s)}/${enc(st)}/ml/${path}`),
    mlAction: (s, st, programId, action, body) =>
      request("POST", `/s/${enc(s)}/${enc(st)}/ml/programs/${enc(programId)}/actions/${enc(action)}`, body),
    intelligencePolicy: (s, st) => request("GET", `/s/${enc(s)}/${enc(st)}/intelligence/policy`),
    updateIntelligencePolicy: (s, st, body) => request("PUT", `/s/${enc(s)}/${enc(st)}/intelligence/policy`, body),
    simulateIntelligence: (s, st, body) => request("POST", `/s/${enc(s)}/${enc(st)}/intelligence/simulate`, body),
  };
}
